import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { OpportunitesTabs } from "@/components/TalentSectionTabs";
import { Inbox, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_LABEL = {
  sent: { label: "Envoyée", color: "bg-[#EEF3FF] text-[#3B5BDB] border-[#BFDBFE]" },
  viewed: { label: "Vue", color: "bg-[#FAF3EA] text-[#6E4B2A] border-[#E6C29A]" },
  shortlisted: { label: "Shortlistée", color: "bg-[#1A1A1A] text-[#D4A574] border-[#1A1A1A]" },
  accepted: { label: "Acceptée", color: "bg-[#E7F5EC] text-[#2E7D32] border-[#BFE4C5]" },
  refused: { label: "Non retenue", color: "bg-[#FEEFEC] text-[#A3483F] border-[#F2C0B7]" },
};

export default function MyApplications() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [chatSummary, setChatSummary] = useState({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (user?.role && user.role !== "talent") navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.role === "talent") {
      api
        .get("/my/applications")
        .then(({ data }) => setApps(data || []))
        .finally(() => setReady(true));
      api.get("/my/collaborations/summary")
        .then(({ data }) => {
          const map = {};
          (data.items || []).forEach((it) => { map[it.collaboration.id] = it; });
          setChatSummary(map);
        })
        .catch(() => {});
    }
  }, [user]);

  if (!user) return null;

  return (
    <DashboardLayout>
      <OpportunitesTabs />

      {ready && apps.length === 0 && (
        <div className="bg-white rounded-2xl border border-[#EADDC9] p-12 text-center" data-testid="apps-empty-state">
          <div className="w-14 h-14 rounded-full bg-[#F5E6D0] flex items-center justify-center mx-auto mb-4">
            <Inbox className="w-6 h-6 text-[#C68E59]" />
          </div>
          <p className="font-serif text-xl text-[#1A1A1A] mb-2">Aucune candidature pour l'instant</p>
          <p className="text-sm text-[#8B8680] mb-6">
            Explorez les collaborations et postulez pour démarrer votre carrière.
          </p>
          <Link to="/collaborations">
            <button className="inline-flex items-center gap-2 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white text-xs font-semibold tracking-wide px-5 py-2.5 transition" data-testid="apps-empty-cta">
              Explorer les collaborations
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </Link>
        </div>
      )}

      <div className="space-y-4">
        {apps.map((a) => {
          const s = STATUS_LABEL[a.status] || STATUS_LABEL.sent;
          return (
            <div
              key={a.id}
              className="bg-white rounded-2xl border border-[#EADDC9] p-6 flex flex-col gap-4"
              data-testid={`app-${a.id}`}
            >
              <div className="flex flex-wrap items-start gap-4 justify-between">
                <div className="min-w-0">
                  <p className="caption-label text-[#C68E59] capitalize">{a.collaboration?.project_type}</p>
                  <Link
                    to={`/collaborations/${a.collaboration_id}`}
                    className="font-serif text-2xl text-[#1A1A1A] hover:underline block truncate"
                  >
                    {a.collaboration?.title || "Collaboration"}
                  </Link>
                  <p className="text-sm text-[#8B8680] mt-1">
                    Envoyée le{" "}
                    {new Date(a.created_at).toLocaleDateString("fr-FR", {
                      day: "numeric", month: "long", year: "numeric",
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${s.color}`} data-testid={`app-status-${a.status}`}>
                    {s.label}
                  </span>
                  {a.status === "accepted" && a.mission_id && (
                    <Link to={`/missions/${a.mission_id}`}>
                      <Button className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-9 text-xs" data-testid={`open-mission-${a.id}`}>
                        Voir la mission
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
              {/* Discussion preview — only for accepted applications */}
              {a.status === "accepted" && chatSummary[a.collaboration_id] && (
                <Link
                  to={`/collaborations/${a.collaboration_id}?tab=discussion`}
                  className="flex items-center gap-3 rounded-2xl border border-[#EADDC9] bg-[#FAF3EA]/40 hover:bg-[#FAF3EA] px-3 py-2.5 transition-colors group/chat"
                  data-testid={`app-chat-preview-${a.id}`}
                >
                  <div className="relative flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-[#A26F3E]" />
                    {chatSummary[a.collaboration_id].unread_count > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#C68E59] text-white text-[9px] font-semibold flex items-center justify-center" data-testid={`app-unread-${a.id}`}>
                        {chatSummary[a.collaboration_id].unread_count > 9 ? "9+" : chatSummary[a.collaboration_id].unread_count}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {chatSummary[a.collaboration_id].last_message ? (
                      <>
                        <p className={`text-xs truncate ${chatSummary[a.collaboration_id].unread_count > 0 ? "text-[#1A1A1A] font-medium" : "text-[#6E4B2A]"}`}>
                          {chatSummary[a.collaboration_id].last_message.body || "📎 Pièce jointe"}
                        </p>
                        <p className="text-[10px] text-[#A26F3E] mt-0.5">
                          {new Date(chatSummary[a.collaboration_id].last_message.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} ·{" "}
                          {new Date(chatSummary[a.collaboration_id].last_message.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-[#A26F3E]">Démarrer la discussion</p>
                    )}
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-[#A26F3E] group-hover/chat:text-[#1A1A1A] transition-colors" />
                </Link>
              )}
              {a.status === "refused" && a.refusal_message && (
                <div className="rounded-xl border border-[#F2C0B7] bg-[#FEEFEC] p-4 text-sm text-[#A3483F]" data-testid={`app-refusal-${a.id}`}>
                  <p className="caption-label text-[#A3483F] mb-1">Mot de la marque</p>
                  <p className="leading-relaxed">{a.refusal_message}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
